import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Star } from 'lucide-react';
import Header from '../components/Header';
import AdminLoginModal from '../components/AdminLoginModal';
import SEOHead, { reviewSchema, breadcrumbSchema } from '../components/SEOHead';
import { Reveal } from '../components/motion/Reveal';
import { Magnetic } from '../components/motion/Magnetic';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Stars = ({ className = '' }) => (
  <span className={`inline-flex items-center gap-0.5 ${className}`} aria-label="5 out of 5 stars">
    {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={12} fill="currentColor" strokeWidth={0} />)}
  </span>
);

const ReviewRow = ({ review, index }) => {
  const flip = index % 2 === 1;
  return (
    <Reveal
      as="article"
      className={`relative grid grid-cols-12 gap-6 py-14 md:py-20 border-t border-black/10 ${flip ? 'md:text-right' : ''}`}
      data-testid={`review-row-${index}`}
    >
      <span className={`col-span-12 md:col-span-2 font-presti text-5xl md:text-7xl leading-none text-black/[0.08] select-none ${flip ? 'md:order-2 md:text-left' : ''}`}>
        {String(index + 1).padStart(2, '0')}
      </span>
      <div className={`col-span-12 md:col-span-9 ${flip ? 'md:order-1 md:col-start-1' : 'md:col-start-4'}`}>
        <blockquote className="font-presti text-2xl sm:text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.25] text-black/90 mb-8">
          &ldquo;{review.text}&rdquo;
        </blockquote>
        <footer className={`flex flex-col gap-1 ${flip ? 'md:items-end' : ''}`}>
          <Stars className="text-black/70 mb-1" />
          <p className="text-[11px] tracking-[0.3em] uppercase text-black/80" data-testid={`review-name-${index}`}>{review.name}</p>
          {review.role && <p className="text-xs text-black/40 tracking-wide">{review.role}</p>}
        </footer>
      </div>
    </Reveal>
  );
};

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/content/testimonials`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/content/settings`).then((r) => r.json()).catch(() => ({})),
    ]).then(([t, s]) => {
      setReviews(Array.isArray(t) ? t : []);
      setSettings(s || {});
    }).finally(() => setLoading(false));
  }, []);

  const logoUrl = settings.photo_logo_url || settings.logo_url || '';
  const googleUrl = settings.google_reviews_url || '';

  return (
    <main className="bg-[#fafafa] min-h-screen text-black" data-testid="reviews-page">
      <SEOHead
        title="Reviews — What Our Couples Say"
        description={`Read ${reviews.length || ''} five-star reviews from couples who trusted Crystal Media with their wedding photography and films — Pakistani, Indian, Sikh, Muslim, Hindu and multicultural weddings across Toronto, Canada and the USA.`}
        url="/reviews"
        keywords={['Crystal Media reviews', 'wedding photographer reviews Toronto', 'best South Asian wedding videographer reviews']}
        siteSettings={settings}
        jsonLd={[
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Reviews', path: '/reviews' }]),
          ...(reviews.length ? [reviewSchema(reviews)] : []),
        ]}
      />
      <Header logoUrl={logoUrl} variant="light" />

      <section className="pt-36 md:pt-44 pb-10 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="text-[10px] tracking-[0.4em] uppercase text-black/40 mb-6">Kind Words</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-presti text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-black max-w-3xl" data-testid="reviews-heading">
              What our couples say when the dust settles
            </h1>
          </Reveal>
          <Reveal delay={0.16} className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10">
            <div className="flex items-center gap-3">
              <Stars className="text-black" />
              <span className="text-sm text-black/70" data-testid="reviews-count">
                5.0 · {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
              </span>
            </div>
            {googleUrl && (
              <a
                href={googleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-black/60 hover:text-black border-b border-black/20 hover:border-black pb-1 transition-colors"
                data-testid="reviews-google-link"
              >
                Read on Google <ExternalLink size={12} />
              </a>
            )}
          </Reveal>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <p className="py-20 text-center text-xs tracking-[0.3em] uppercase text-black/30">Loading…</p>
          ) : reviews.length === 0 ? (
            <p className="py-20 text-center text-xs tracking-[0.3em] uppercase text-black/30" data-testid="reviews-empty">No reviews yet</p>
          ) : (
            reviews.map((r, i) => <ReviewRow key={r.id || i} review={r} index={i} />)
          )}
        </div>
      </section>

      <section className="bg-black text-white py-24 md:py-32 px-6 text-center">
        <Reveal className="max-w-2xl mx-auto">
          <h2 className="font-presti text-3xl sm:text-4xl md:text-5xl leading-[1.1] mb-5">Your story could be next</h2>
          <p className="text-white/50 text-sm md:text-base mb-10">We take a limited number of weddings each year. Tell us about yours.</p>
          <Magnetic>
            <Link
              to="/inquire"
              className="inline-flex items-center gap-3 px-10 py-4 bg-white text-black text-xs tracking-[0.25em] uppercase hover:bg-white/90 transition-colors"
              data-testid="reviews-inquire-btn"
            >
              Inquire Now <ArrowRight size={14} />
            </Link>
          </Magnetic>
        </Reveal>
      </section>

      <footer className="py-10 px-6 border-t border-black/5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p
            onClick={(e) => { if (e.detail === 3) setShowLogin(true); }}
            className="text-[10px] tracking-[0.2em] uppercase text-black/30 cursor-default select-none"
            data-testid="footer-copyright-reviews"
          >
            © {new Date().getFullYear()} Crystal Media. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            {[['/photo', 'Photo'], ['/film', 'Film'], ['/about', 'About'], ['/inquire', 'Inquire']].map(([p, l]) => (
              <Link key={p} to={p} className="text-[10px] tracking-[0.2em] uppercase text-black/40 hover:text-black transition-colors">{l}</Link>
            ))}
          </div>
        </div>
      </footer>
      <AdminLoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
    </main>
  );
}
